import { getCompliancePerformanceOverview } from '@/api/dashboardApi'
import {
  CompletedOnTimeIcon,
  CompletedWithDelayIcon,
  InProgressIcon,
  OverdueIcon,
} from '@/assets/icons/index'
import { complianceStatusUIMetaData } from '@/constants/dashboardConstant'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Tooltip, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

// ==================== Constants ====================
const statusIconMap = {
  success: <CompletedOnTimeIcon />,
  warning: <CompletedWithDelayIcon />,
  error: <OverdueIcon />,
  info: <InProgressIcon />,
}

// ==================== Helpers ====================
const mapCompliancePerformanceToData = (data = {}) => [
  { label: 'On-time', value: data?.onTime, status: 'success' },
  { label: 'Delayed', value: data?.delayed, status: 'warning' },
  { label: 'Overdue', value: data?.overdue, status: 'error' },
  { label: 'Upcoming', value: data?.upcoming, status: 'info' },
]

const CompliancePerformanceOverview = () => {
  // ==================== Hooks ====================
  const optionsLoaded = useSelector(
    state => state.dashboardFilter.optionsLoaded
  )
  const appliedFilters = useSelector(
    state => state.dashboardFilter.appliedFilters
  )

  // ==================== State ====================
  const [data, setData] = useState([])
  const [dataLoaded, setDataLoaded] = useState(false)

  // ==================== Effects ====================
  useEffect(() => {
    if (!optionsLoaded) return
    const fetchData = async () => {
      setDataLoaded(false)
      try {
        const complianceResponse =
          await getCompliancePerformanceOverview(appliedFilters)
        processData(complianceResponse)
      } finally {
        setDataLoaded(true)
      }
    }
    fetchData()
  }, [optionsLoaded, appliedFilters])

  // ==================== Component Helpers ====================
  const processData = complianceResponse => {
    const complianceData = complianceResponse?.data?.data
    if (complianceResponse?.success && complianceData) {
      setData(mapCompliancePerformanceToData(complianceData))
    } else {
      setData([])
    }
  }

  // ==================== Return ====================
  return (
    <Box
      sx={{
        border: '1px solid var(--border-subtel)',
        backgroundColor: '#fff',
        flex: '1 1 0',
        minWidth: '300px',
        height: '323px',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        component="section"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 500,
            color: 'var(--forbrand-primary)',
            lineHeight: 1.4,
          }}
        >
          {complianceStatusUIMetaData.heading}
        </Typography>

        <Tooltip title={complianceStatusUIMetaData.subHeading}>
          <InfoOutlinedIcon sx={{ width: '17.5px', height: '17.47px' }} />
        </Tooltip>
      </Box>

      {!dataLoaded ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              color: 'var(--forbrand-primary)',
              fontSize: 'var(--font-size-lg)',
            }}
          >
            Loading...
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 3,
          }}
        >
          {data.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {statusIconMap[item.status]}
                <Typography
                  sx={{
                    fontSize: 'var(--font-size-md)',
                    fontWeight: 400,
                    color: 'var(--forbrand-primary)',
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 500,
                  color: 'var(--forbrand-primary)',
                }}
              >
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default CompliancePerformanceOverview
